import { BaseSnapshotProps } from "@custom-types/base-types";
import {
  neutral,
  NO_TRANSLATE_CLASS,
  TeamIcon,
  UserCardItem,
  useToast,
} from "@flat-ui";
import { Box } from "@mui/material";
import { useMembersUtils } from "@hooks/use-member-utils";
import { TEAM_DISPLAY_NAME } from "@src/constants/team-constants";
import { FaroCoreApiTypes } from "@stellar/core-api-client";
import { isUserSnapshotAdmin } from "@utils/permission-control/snapshot-permission-control";
import { capitalizeFirstLetter } from "@utils/string-utils";
import { isTeam } from "@utils/team-utils";
import { getPendingUserIcon, isMemberActive } from "@utils/user-utils";

export interface SelectedCardItemOption {
  /** The target element of the selected option */
  target: { value: string };
}

interface Props extends BaseSnapshotProps {
  /** The member of snapshot to render */
  member: FaroCoreApiTypes.ProjectMemberBase;

  /** Callback function when an option is selected from dropdown */
  onSelectOption(
    option: SelectedCardItemOption,
    member: FaroCoreApiTypes.ProjectMemberBase
  ): void;
}

/** Component that renders a snapshot member using the UserCardItem component */
export function SnapshotMember({
  member,
  snapshot,
  onSelectOption,
}: Props): JSX.Element {
  const { openToast } = useToast();
  const { openProfilePage, openViewTeamMembers } = useMembersUtils();

  /** Is the member a team */
  const isMemberTeam = isTeam(member);

  // Hide delete option if user is not admin of the snapshot
  const shouldHideRemoveOption = !isUserSnapshotAdmin(snapshot);

  /**
   * Opens the profile page of the user or the team page members.
   * The permission validation is done in the canOpenProfilePage variable.
   */
  function openProfileOrTeamMembersClick(
    member: FaroCoreApiTypes.ProjectMemberBase
  ): void {
    if (isMemberTeam) {
      if (member?.id) {
        openViewTeamMembers({
          teamId: member.id,
        });
      } else {
        openToast({
          title: `${capitalizeFirstLetter(
            TEAM_DISPLAY_NAME
          )} profile page cannot be opened`,
          message: `The ${TEAM_DISPLAY_NAME} ID is not available. Please reload the page to try again.`,
          variant: "error",
        });
      }
    } else {
      openProfilePage({
        member,
        source: "user more info menu snapshot member list",
      });
    }
  }

  return (
    <Box className={NO_TRANSLATE_CLASS}>
      <UserCardItem
        key={member.identity}
        shouldShowProfileCard={true}
        actionButtonLabel={isMemberTeam ? "View Members" : "Profile"}
        onProfileCardClick={() => openProfileOrTeamMembersClick(member)}
        shouldHideRemove={shouldHideRemoveOption}
        icon={isMemberTeam ? <TeamIcon /> : getPendingUserIcon(member)}
        displayOption={isMemberActive(member) ? "nameOnly" : "emailOnly"}
        avatarSx={isMemberTeam ? { bgcolor: neutral[700] } : undefined}
        userInfo={{
          name: member.name,
          identity: member.identity,
          email: isMemberTeam ? "" : member.email,
          profileImageUrl: member.thumbnailUrl,
          userRole: "Snapshot Viewer",
        }}
        // We are showing on one option as all member has viewer access to the snapshot
        selectedOption="RESOURCE_VIEWER"
        dropdownOptions={[
          {
            key: "RESOURCE_VIEWER",
            label: "Snapshot Viewer",
            value: "RESOURCE_VIEWER",
          },
        ]}
        onChange={(option) => onSelectOption(option, member)}
      />
    </Box>
  );
}
